/**
 * The plan's ink at full resolution, for placing a border exactly on a line.
 *
 * The rule (Ricky's): the colour sits on the OUTSIDE of the plan's black line —
 * its inner edge just inside the line's outer edge, so no paper ever shows
 * between colour and line, and the rest of the band lies beyond the line. A
 * mark's border therefore runs along the outer edge of the ink, `overlapFor`
 * inside it. The rails give a first estimate (the line's centre and weight);
 * the luminance profile across the line gives the edge to a fraction of a
 * pixel, and where the ink is not a lone clean line (a leader touching it, a
 * tick, two lines merging, a gap) the estimate stands in.
 */
import type { Vec } from '../../shared/doc.ts';
import { offsetSide } from '../editor/outline.ts';
import { simplify } from '../editor/geom.ts';
import { luminance } from './raster.ts';

export interface Luma {
  w: number;
  h: number;
  data: Uint8Array;
}

export const MAX_LUMA_PX = 20_000_000; // beyond this a plan is followed by its rails alone

export function lumaFromRgba(rgba: Uint8ClampedArray, w: number, h: number): Luma {
  return { w, h, data: luminance(rgba, w * h) };
}

/** Luminance at a position in image space (a pixel's colour sits at its centre, i + 0.5), bilinear; paper beyond the image. */
export function lumaAt(l: Luma, x: number, y: number): number {
  const xs = x - 0.5,
    ys = y - 0.5;
  const x0 = Math.floor(xs),
    y0 = Math.floor(ys);
  if (x0 < 0 || y0 < 0 || x0 + 1 >= l.w || y0 + 1 >= l.h) return 255;
  const fx = xs - x0,
    fy = ys - y0;
  const i = y0 * l.w + x0;
  const d = l.data;
  return (d[i]! * (1 - fx) + d[i + 1]! * fx) * (1 - fy) + (d[i + l.w]! * (1 - fx) + d[i + l.w + 1]! * fx) * fy;
}

/** How far inside the line's outer edge the colour begins: a hair, scaled with the line but never a gap. */
export function overlapFor(thick: number): number {
  return Math.max(0.5, Math.min(1.2, 0.3 * thick));
}
/** Where a border sits relative to a line's centre when only the line's weight is known. */
export function edgeOffset(thick: number): number {
  return Math.max(0, thick / 2 - overlapFor(thick));
}

const STEP = 0.25; // px along the profile
const SHARP_COS = Math.cos((25 * Math.PI) / 180); // a turn this sharp within a dozen px is a kink, never a corridor's curve
const HOOK_SAMPLES = 8; // a run between two kinks this short (≤ 16 px) is a hook

/** Harness diagnostics: what the last `refineSide` calls saw. */
export const inkStats: { calls: number; samples: number; measured: number; kept: number; last: { thick: number; typical: number; measured: number; kept: number; n: number }[] } = { calls: 0, samples: 0, measured: 0, kept: 0, last: [] };

/**
 * The border's signed offset along `n` (unit, pointing away from the mark)
 * from p, a point on or near a line about `thick` px wide: the ink's outer
 * edge less the overlap. Null when the ink under p is not one clean line.
 */
export function inkEdge(l: Luma, p: Vec, n: Vec, thick: number): number | null {
  return inkEdgeRun(l, p, n, thick)?.edge ?? null;
}

/** As `inkEdge`, with the measured width of the ink run (to tell a clean stretch of line from a merge). */
export function inkEdgeRun(l: Luma, p: Vec, n: Vec, thick: number): { edge: number; width: number } | null {
  const reach = thick + 4;
  const count = Math.round((2 * reach) / STEP) + 1;
  const prof = new Float32Array(count);
  let lo = 255,
    hi = 0;
  for (let i = 0; i < count; i++) {
    const t = -reach + i * STEP;
    const v = lumaAt(l, p.x + n.x * t, p.y + n.y * t);
    prof[i] = v;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (hi - lo < 60) return null; // no line here
  const level = (lo + hi) / 2;
  const i0 = Math.round(reach / STEP);
  const maxD = Math.round((0.6 * thick + 1) / STEP);
  let seed = -1;
  for (let d = 0; d <= maxD && seed < 0; d++) {
    if (i0 + d < count && prof[i0 + d]! < level) seed = i0 + d;
    else if (i0 - d >= 0 && prof[i0 - d]! < level) seed = i0 - d;
  }
  if (seed < 0) return null;
  let a = seed,
    b = seed;
  while (a > 0 && prof[a - 1]! < level) a--;
  while (b + 1 < count && prof[b + 1]! < level) b++;
  if (a === 0 || b === count - 1) return null; // the run leaves the window: not a lone line
  const cross = (i: number, j: number): number => {
    const y0 = prof[i]!,
      y1 = prof[j]!;
    return i + (level - y0) / (y1 - y0 || 1);
  };
  const inner = cross(a - 1, a),
    outer = cross(b, b + 1);
  const width = (outer - inner) * STEP;
  if (width < 0.5 * thick || width > 1.6 * thick + 0.5) return null; // a junction, a tick, two lines merged
  return { edge: (outer - i0) * STEP - overlapFor(thick), width };
}

/**
 * A rail side (in travel order) turned into the border beside it: sampled every
 * ~2 px, each sample placed at the ink's outer edge less the overlap on the
 * `outward` side (+1 = left of travel), gaps where the ink is not a clean line
 * bridged from their neighbours (or the weight-based offset when long), then
 * lightly smoothed and simplified. Without ink data the weight-based offset alone.
 * `fallback` is the offset assumed where nothing can be read (the weight-based one for a rail's
 * centre line; 0 for points that already lie on the border). With `bridge` = 'position', short
 * unreadable runs are re-placed on the line between their read neighbours (for a side that is
 * already on the border and only needs its seams cleaned).
 */
export function refineSide(l: Luma | null, pts: number[], outward: 1 | -1, thick: number, fallback = edgeOffset(thick), bridge: 'offset' | 'position' = 'offset'): number[] {
  if (!l || pts.length < 4) return offsetSide(pts, outward * fallback);
  const rs = resample(pts, 2);
  const n = rs.length / 2;
  if (n < 2) return offsetSide(pts, outward * fallback);
  const nx = new Float64Array(n),
    ny = new Float64Array(n),
    d = new Float64Array(n),
    wd = new Float64Array(n);
  const ok = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const p = Math.max(0, i - 1),
      q = Math.min(n - 1, i + 1);
    let tx = rs[2 * q]! - rs[2 * p]!,
      ty = rs[2 * q + 1]! - rs[2 * p + 1]!;
    const tl = Math.hypot(tx, ty) || 1;
    tx /= tl;
    ty /= tl;
    nx[i] = -ty * outward;
    ny[i] = tx * outward;
    const e = inkEdgeRun(l, { x: rs[2 * i]!, y: rs[2 * i + 1]! }, { x: nx[i]!, y: ny[i]! }, thick);
    if (e && Math.abs(e.edge - fallback) <= 0.6 * thick + 1.5) {
      d[i] = e.edge;
      wd[i] = e.width;
      ok[i] = 1;
    }
  }
  // A line keeps one width along a side. Where the run under a sample is wider than the side's own
  // typical width (a leader merging in at a shallow angle, a tick, a symbol) the outer edge measured
  // is not the line's: such samples are bridged from the clean stretches either side.
  const widths: number[] = [];
  for (let i = 0; i < n; i++) if (ok[i]) widths.push(wd[i]!);
  const measured = widths.length;
  let typical = 0;
  if (widths.length >= 4) {
    widths.sort((a, b) => a - b);
    typical = widths[widths.length >> 1]!;
    const slack = Math.max(0.4, 0.15 * typical);
    for (let i = 0; i < n; i++) if (ok[i] && Math.abs(wd[i]! - typical) > slack) ok[i] = 0;
  }
  // A reading that disagrees with its neighbours' by more than a pixel or so read another
  // line (a leader passing close by, a symbol's stroke): it is dropped like an unreadable one.
  const OUT = Math.max(1, 0.3 * thick);
  const bad = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    if (!ok[i]) continue;
    const around: number[] = [];
    for (let j = Math.max(0, i - 4); j <= Math.min(n - 1, i + 4); j++) if (j !== i && ok[j]) around.push(d[j]!);
    if (around.length < 3) continue;
    around.sort((a, b) => a - b);
    const med = around[around.length >> 1]!;
    if (Math.abs(d[i]! - med) > OUT) bad[i] = 1;
  }
  for (let i = 0; i < n; i++) if (bad[i]) ok[i] = 0;
  let kept = 0;
  for (let i = 0; i < n; i++) if (ok[i]) kept++;
  inkStats.calls++;
  inkStats.samples += n;
  inkStats.measured += measured;
  inkStats.kept += kept;
  inkStats.last.push({ thick, typical, measured, kept, n });
  if (inkStats.last.length > 12) inkStats.last.shift();
  // bridge short gaps from the measured neighbours; long ones keep the weight-based offset
  const MAX_GAP = 20;
  let i = 0;
  while (i < n) {
    if (ok[i]) {
      i++;
      continue;
    }
    let j = i;
    while (j < n && !ok[j]) j++;
    const before = i > 0 ? d[i - 1]! : null,
      after = j < n ? d[j]! : null;
    const len = j - i;
    for (let k = i; k < j; k++) {
      if (before !== null && after !== null && len <= MAX_GAP) d[k] = before + ((after - before) * (k - i + 1)) / (len + 1);
      else if (len <= MAX_GAP && (before !== null || after !== null)) d[k] = before ?? after!;
      else d[k] = fallback;
    }
    i = j;
  }
  // a 3-tap smoothing takes the last of the sampling noise out of the edge
  const sm = new Float64Array(n);
  for (let k = 0; k < n; k++) sm[k] = k === 0 || k === n - 1 ? d[k]! : (d[k - 1]! + 2 * d[k]! + d[k + 1]!) / 4;
  const out: number[] = [];
  for (let k = 0; k < n; k++) out.push(rs[2 * k]! + nx[k]! * sm[k]!, rs[2 * k + 1]! + ny[k]! * sm[k]!);
  if (bridge === 'position') {
    // A side never doubles back on itself within a few pixels: a short run bounded by sharp turns
    // is a hook — the skeleton bending into a leader or a symbol at a junction — and is dropped
    // as if it could not be read (its ink is another line's).
    const sharp = new Uint8Array(n);
    const W = 3; // the direction is taken over 3 samples (6 px) either side, so a bend spread over a few samples still counts
    for (let k = 1; k < n - 1; k++) {
      const p = Math.max(0, k - W),
        q = Math.min(n - 1, k + W);
      const ax = rs[2 * k]! - rs[2 * p]!,
        ay = rs[2 * k + 1]! - rs[2 * p + 1]!,
        bx = rs[2 * q]! - rs[2 * k]!,
        by = rs[2 * q + 1]! - rs[2 * k + 1]!;
      const c = (ax * bx + ay * by) / ((Math.hypot(ax, ay) || 1) * (Math.hypot(bx, by) || 1));
      if (c < SHARP_COS) sharp[k] = 1;
    }
    let runStart = 0;
    for (let k = 0; k <= n; k++) {
      if (k < n && !sharp[k]) continue;
      // the run [runStart, k) ends at a sharp turn (or the side's end); the turn itself goes too
      if (k < n) ok[k] = 0;
      const bounded = (runStart === 0 || sharp[runStart - 1]) && (k === n || sharp[k]);
      if (bounded && k - runStart <= HOOK_SAMPLES && !(runStart === 0 && k === n)) for (let q = Math.max(0, runStart - 1); q <= Math.min(n - 1, k); q++) ok[q] = 0;
      runStart = k + 1;
    }
    // points that already lay on the border but could not be read (a seam's stray point, a
    // junction) are put on the straight line between their nearest read neighbours instead of
    // keeping their own place; long unreadable runs (a step across the band) stay as they are
    let i = 0;
    while (i < n) {
      if (ok[i]) {
        i++;
        continue;
      }
      let j = i;
      while (j < n && !ok[j]) j++;
      const len = j - i;
      if (i > 0 && j < n && len <= MAX_GAP) {
        const ax = out[2 * (i - 1)]!,
          ay = out[2 * (i - 1) + 1]!,
          bx = out[2 * j]!,
          by = out[2 * j + 1]!;
        for (let k = i; k < j; k++) {
          const t = (k - i + 1) / (len + 1);
          out[2 * k] = ax + (bx - ax) * t;
          out[2 * k + 1] = ay + (by - ay) * t;
        }
      }
      i = j;
    }
  }
  return simplify(out, 0.15);
}

/** A polyline resampled every `step` px (both ends kept). */
function resample(pts: number[], step: number): number[] {
  const n = pts.length / 2;
  if (n < 2) return pts.slice();
  const cum = [0];
  for (let i = 1; i < n; i++) cum.push(cum[i - 1]! + Math.hypot(pts[2 * i]! - pts[2 * i - 2]!, pts[2 * i + 1]! - pts[2 * i - 1]!));
  const total = cum[n - 1]!;
  const m = Math.max(1, Math.round(total / step));
  const out: number[] = [];
  let k = 0;
  for (let i = 0; i <= m; i++) {
    const s = (i / m) * total;
    while (k < n - 2 && cum[k + 1]! < s) k++;
    const l = cum[k + 1]! - cum[k]! || 1;
    const t = Math.max(0, Math.min(1, (s - cum[k]!) / l));
    out.push(pts[2 * k]! + (pts[2 * k + 2]! - pts[2 * k]!) * t, pts[2 * k + 1]! + (pts[2 * k + 3]! - pts[2 * k + 1]!) * t);
  }
  return out;
}
